'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { type ConsentInput, isGpcActive, parseConsentCookie, writeConsentCookie } from '@/lib/consentCookie';
import { isPublicWatchPagePath } from '@/lib/publicWatchPage';
import { isAuthFlowPath } from '@/lib/consentBannerPaths';
import styles from './ConsentBanner.module.css';
type View = 'hidden' | 'main' | 'prefs';
const STRINGS: Record<string, Record<string, string>> = {
  en: {
    bannerText: 'We use cookies to improve your experience and analyze site traffic.',
    cookiePolicy: 'Cookie Policy',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    manage: 'Manage Preferences',
    save: 'Save Preferences',
    prefsTitle: 'Cookie Preferences',
    necessary: 'Necessary',
    necessaryDesc: 'Required for the site to function. Always enabled.',
    alwaysOn: 'Always on',
    analytics: 'Analytics',
    analyticsDesc: 'Help us understand how visitors use our site.',
    marketing: 'Marketing',
    marketingDesc: 'Used to deliver relevant ads and measure campaign performance.'
  },
  es: {
    bannerText: 'Usamos cookies para mejorar tu experiencia y analizar el tráfico del sitio.',
    cookiePolicy: 'Política de cookies',
    acceptAll: 'Aceptar todo',
    rejectAll: 'Rechazar todo',
    manage: 'Gestionar preferencias',
    save: 'Guardar preferencias',
    prefsTitle: 'Preferencias de cookies',
    necessary: 'Necesarias',
    necessaryDesc: 'Necesarias para el funcionamiento del sitio. Siempre activas.',
    alwaysOn: 'Siempre activas',
    analytics: 'Analíticas',
    analyticsDesc: 'Nos ayudan a entender cómo los visitantes usan nuestro sitio.',
    marketing: 'Marketing',
    marketingDesc: 'Se usan para mostrar anuncios relevantes y medir campañas.'
  },
  'pt-BR': {
    bannerText: 'Usamos cookies para melhorar sua experiência e analisar o tráfego do site.',
    cookiePolicy: 'Política de cookies',
    acceptAll: 'Aceitar tudo',
    rejectAll: 'Rejeitar tudo',
    manage: 'Gerenciar preferências',
    save: 'Salvar preferências',
    prefsTitle: 'Preferências de cookies',
    necessary: 'Necessários',
    necessaryDesc: 'Necessários para o funcionamento do site. Sempre ativos.',
    alwaysOn: 'Sempre ativos',
    analytics: 'Analíticos',
    analyticsDesc: 'Nos ajudam a entender como os visitantes usam nosso site.',
    marketing: 'Marketing',
    marketingDesc: 'Usados para exibir anúncios relevantes e medir campanhas.'
  },
  'zh-CN': {
    bannerText: '我们使用 Cookie 来改善您的体验并分析网站流量。',
    cookiePolicy: 'Cookie 政策',
    acceptAll: '全部接受',
    rejectAll: '全部拒绝',
    manage: '管理偏好',
    save: '保存偏好',
    prefsTitle: 'Cookie 偏好设置',
    necessary: '必要',
    necessaryDesc: '网站正常运行所必需，始终启用。',
    alwaysOn: '始终启用',
    analytics: '分析',
    analyticsDesc: '帮助我们了解访客如何使用网站。',
    marketing: '营销',
    marketingDesc: '用于展示相关广告并衡量营销活动效果。'
  },
  ru: {
    bannerText: 'Мы используем cookie для улучшения вашего опыта и анализа трафика.',
    cookiePolicy: 'Политика cookie',
    acceptAll: 'Принять все',
    rejectAll: 'Отклонить все',
    manage: 'Настроить',
    save: 'Сохранить',
    prefsTitle: 'Настройки cookie',
    necessary: 'Необходимые',
    necessaryDesc: 'Необходимы для работы сайта. Всегда включены.',
    alwaysOn: 'Всегда включены',
    analytics: 'Аналитика',
    analyticsDesc: 'Помогают понять, как посетители используют сайт.',
    marketing: 'Маркетинг',
    marketingDesc: 'Используются для показа релевантной рекламы и оценки кампаний.'
  },
  vi: {
    bannerText: 'Chúng tôi sử dụng cookie để cải thiện trải nghiệm và phân tích lưu lượng truy cập.',
    cookiePolicy: 'Chính sách cookie',
    acceptAll: 'Chấp nhận tất cả',
    rejectAll: 'Từ chối tất cả',
    manage: 'Quản lý tùy chọn',
    save: 'Lưu tùy chọn',
    prefsTitle: 'Tùy chọn cookie',
    necessary: 'Cần thiết',
    necessaryDesc: 'Cần thiết để trang web hoạt động. Luôn bật.',
    alwaysOn: 'Luôn bật',
    analytics: 'Phân tích',
    analyticsDesc: 'Giúp chúng tôi hiểu cách khách truy cập sử dụng trang web.',
    marketing: 'Tiếp thị',
    marketingDesc: 'Dùng để hiển thị quảng cáo phù hợp và đo lường chiến dịch.'
  },
  fr: {
    bannerText: 'Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic.',
    cookiePolicy: 'Politique de cookies',
    acceptAll: 'Tout accepter',
    rejectAll: 'Tout refuser',
    manage: 'Gérer les préférences',
    save: 'Enregistrer',
    prefsTitle: 'Préférences de cookies',
    necessary: 'Nécessaires',
    necessaryDesc: 'Indispensables au fonctionnement du site. Toujours activés.',
    alwaysOn: 'Toujours activés',
    analytics: 'Analytiques',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs utilisent le site.',
    marketing: 'Marketing',
    marketingDesc: 'Utilisés pour afficher des publicités pertinentes et mesurer les campagnes.'
  },
  it: {
    bannerText: 'Utilizziamo i cookie per migliorare la tua esperienza e analizzare il traffico.',
    cookiePolicy: 'Informativa sui cookie',
    acceptAll: 'Accetta tutto',
    rejectAll: 'Rifiuta tutto',
    manage: 'Gestisci preferenze',
    save: 'Salva preferenze',
    prefsTitle: 'Preferenze cookie',
    necessary: 'Necessari',
    necessaryDesc: 'Necessari per il funzionamento del sito. Sempre attivi.',
    alwaysOn: 'Sempre attivi',
    analytics: 'Analitici',
    analyticsDesc: 'Ci aiutano a capire come i visitatori usano il sito.',
    marketing: 'Marketing',
    marketingDesc: 'Usati per mostrare annunci pertinenti e misurare le campagne.'
  },
  de: {
    bannerText: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und den Traffic zu analysieren.',
    cookiePolicy: 'Cookie-Richtlinie',
    acceptAll: 'Alle akzeptieren',
    rejectAll: 'Alle ablehnen',
    manage: 'Einstellungen verwalten',
    save: 'Einstellungen speichern',
    prefsTitle: 'Cookie-Einstellungen',
    necessary: 'Notwendig',
    necessaryDesc: 'Erforderlich für die Funktion der Website. Immer aktiv.',
    alwaysOn: 'Immer aktiv',
    analytics: 'Analyse',
    analyticsDesc: 'Helfen uns zu verstehen, wie Besucher die Website nutzen.',
    marketing: 'Marketing',
    marketingDesc: 'Werden für relevante Werbung und Kampagnenmessung verwendet.'
  },
  pl: {
    bannerText: 'Używamy plików cookie, aby poprawić Twoje doświadczenia i analizować ruch.',
    cookiePolicy: 'Polityka plików cookie',
    acceptAll: 'Akceptuj wszystkie',
    rejectAll: 'Odrzuć wszystkie',
    manage: 'Zarządzaj preferencjami',
    save: 'Zapisz preferencje',
    prefsTitle: 'Preferencje plików cookie',
    necessary: 'Niezbędne',
    necessaryDesc: 'Wymagane do działania strony. Zawsze włączone.',
    alwaysOn: 'Zawsze włączone',
    analytics: 'Analityczne',
    analyticsDesc: 'Pomagają zrozumieć, jak odwiedzający korzystają ze strony.',
    marketing: 'Marketingowe',
    marketingDesc: 'Służą do wyświetlania trafnych reklam i mierzenia kampanii.'
  },
  nl: {
    bannerText: 'We gebruiken cookies om uw ervaring te verbeteren en verkeer te analyseren.',
    cookiePolicy: 'Cookiebeleid',
    acceptAll: 'Alles accepteren',
    rejectAll: 'Alles weigeren',
    manage: 'Voorkeuren beheren',
    save: 'Voorkeuren opslaan',
    prefsTitle: 'Cookievoorkeuren',
    necessary: 'Noodzakelijk',
    necessaryDesc: 'Vereist voor de werking van de site. Altijd ingeschakeld.',
    alwaysOn: 'Altijd aan',
    analytics: 'Analytisch',
    analyticsDesc: 'Helpen ons te begrijpen hoe bezoekers de site gebruiken.',
    marketing: 'Marketing',
    marketingDesc: 'Gebruikt voor relevante advertenties en campagnemeting.'
  },
  tr: {
    bannerText: 'Deneyiminizi iyileştirmek ve site trafiğini analiz etmek için çerezler kullanıyoruz.',
    cookiePolicy: 'Çerez Politikası',
    acceptAll: 'Tümünü kabul et',
    rejectAll: 'Tümünü reddet',
    manage: 'Tercihleri yönet',
    save: 'Tercihleri kaydet',
    prefsTitle: 'Çerez tercihleri',
    necessary: 'Gerekli',
    necessaryDesc: 'Sitenin çalışması için gereklidir. Her zaman açık.',
    alwaysOn: 'Her zaman açık',
    analytics: 'Analitik',
    analyticsDesc: 'Ziyaretçilerin siteyi nasıl kullandığını anlamamıza yardımcı olur.',
    marketing: 'Pazarlama',
    marketingDesc: 'İlgili reklamlar ve kampanya ölçümü için kullanılır.'
  }
};
function getLocale(): string {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language || 'en';
  if (STRINGS[lang]) return lang;
  const base = lang.split('-')[0];
  if (STRINGS[base]) return base;
  return 'en';
}
function getStrings() {
  return STRINGS[getLocale()] || STRINGS.en;
}
function Toggle({
  checked,
  onChange,
  id,
  label
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
  label: string;
}) {
  return <button type="button" id={id} className={styles.toggle} role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} data-sentry-component="Toggle" data-sentry-source-file="ConsentBanner.tsx">
            <span className={styles.toggleTrack}>
                <span className={styles.toggleThumb} />
            </span>
        </button>;
}
export default function ConsentBanner() {
  const router = useRouter();
  const hideOnPublicWatch = isPublicWatchPagePath(router.pathname) || isPublicWatchPagePath(router.asPath) || isAuthFlowPath(router.pathname) || isAuthFlowPath(router.asPath);
  const [view, setView] = useState<View>('hidden');
  const [visible, setVisible] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLButtonElement>(null);
  const s = getStrings();
  const loadPrefs = useCallback(() => {
    const consent = parseConsentCookie();
    if (consent) {
      setAnalyticsOn(consent.analytics);
      setMarketingOn(consent.marketing);
    }
  }, []);
  const show = useCallback((showPrefs: boolean) => {
    if (isGpcActive()) return;
    loadPrefs();
    setView(showPrefs ? 'prefs' : 'main');
    requestAnimationFrame(() => setVisible(true));
  }, [loadPrefs]);
  const hide = useCallback(() => {
    setVisible(false);
    setTimeout(() => setView('hidden'), 350);
  }, []);
  const accept = useCallback((state: ConsentInput) => {
    writeConsentCookie(state);
    hide();
  }, [hide]);
  useEffect(() => {
    const existing = parseConsentCookie();
    const gpc = isGpcActive();
    if (!hideOnPublicWatch && !gpc && !existing) {
      show(false);
    }
    const handleNeeded = () => {
      if (hideOnPublicWatch) return;
      show(false);
    };
    const handlePrefs = () => {
      if (hideOnPublicWatch) return;
      show(true);
    };
    document.addEventListener('braiv:consent-needed', handleNeeded);
    document.addEventListener('braiv:show-preferences', handlePrefs);
    return () => {
      document.removeEventListener('braiv:consent-needed', handleNeeded);
      document.removeEventListener('braiv:show-preferences', handlePrefs);
    };
  }, [hideOnPublicWatch, show]);
  useEffect(() => {
    if (view !== 'hidden' && firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, [view]);
  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner || view === 'hidden') return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusable = banner!.querySelectorAll<HTMLElement>('button:not([hidden]), a[href], [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    banner.addEventListener('keydown', handleKeyDown);
    return () => banner.removeEventListener('keydown', handleKeyDown);
  }, [view]);
  if (hideOnPublicWatch || view === 'hidden') return null;
  return <div ref={bannerRef} className={`${styles.banner} ${visible ? styles.visible : ''}`} role="dialog" aria-modal="true" aria-label={s.prefsTitle} data-sentry-component="ConsentBanner" data-sentry-source-file="ConsentBanner.tsx">
            <div className={styles.inner}>
                {view === 'main' && <div className={styles.main}>
                        <div className={styles.content}>
                            <p className={styles.text}>
                                {s.bannerText}{' '}
                                <a href="https://www.braiv.co/cookie-policy" target="_blank" rel="noreferrer" className={styles.link}>
                                    {s.cookiePolicy}
                                </a>
                            </p>
                        </div>
                        <div className={styles.actions} role="group" aria-label="Cookie consent actions">
                            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => accept({
            analytics: false,
            marketing: false,
            recording: false
          })} ref={firstFocusRef}>
                                {s.rejectAll}
                            </button>
                            <button type="button" className={`${styles.btn} ${styles.btnText}`} onClick={() => {
            loadPrefs();
            setView('prefs');
          }}>
                                {s.manage}
                            </button>
                            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => accept({
            analytics: true,
            marketing: true,
            recording: true
          })}>
                                {s.acceptAll}
                            </button>
                        </div>
                    </div>}

                {view === 'prefs' && <div className={styles.prefs}>
                        <h2 className={styles.prefsTitle}>{s.prefsTitle}</h2>

                        <div className={styles.category}>
                            <div className={styles.categoryHeader}>
                                <span className={styles.categoryName}>{s.necessary}</span>
                                <span className={styles.alwaysOn}>{s.alwaysOn}</span>
                            </div>
                            <p className={styles.categoryDesc}>{s.necessaryDesc}</p>
                        </div>

                        <div className={styles.category}>
                            <div className={styles.categoryHeader}>
                                <label className={styles.categoryName} htmlFor="consent-toggle-analytics">
                                    {s.analytics}
                                </label>
                                <Toggle id="consent-toggle-analytics" checked={analyticsOn} onChange={setAnalyticsOn} label={s.analytics} />
                            </div>
                            <p className={styles.categoryDesc}>{s.analyticsDesc}</p>
                        </div>

                        <div className={styles.category}>
                            <div className={styles.categoryHeader}>
                                <label className={styles.categoryName} htmlFor="consent-toggle-marketing">
                                    {s.marketing}
                                </label>
                                <Toggle id="consent-toggle-marketing" checked={marketingOn} onChange={setMarketingOn} label={s.marketing} />
                            </div>
                            <p className={styles.categoryDesc}>{s.marketingDesc}</p>
                        </div>

                        <div className={styles.prefsActions}>
                            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} ref={firstFocusRef} onClick={() => accept({
            analytics: analyticsOn,
            marketing: marketingOn,
            recording: analyticsOn
          })}>
                                {s.save}
                            </button>
                        </div>
                    </div>}
            </div>
        </div>;
}